import { Input } from "antd";
import type { KeyboardEvent } from "react";

import { DictSelect } from "@/components/DictSelect";

import type { DetailRowFieldType } from "./types";

/** DetailRow 行内编辑控件入参 */
export interface DetailRowEditControlProps {
  /** 字段类型 */
  type: DetailRowFieldType;
  /** 字段标签，用于生成占位文案 */
  label: string;
  /** 字典类型编码；type 为 dict 时必填 */
  dictCode?: string;
  /** 编辑草稿值 */
  draft: string;
  /** 控件占位 */
  placeholder?: string;
  /** 草稿值变更 */
  onDraftChange: (value: string) => void;
  /** 提交编辑；字典场景可传入选中值 */
  onCommit: (value?: string) => void;
  /** 取消编辑 */
  onCancel: () => void;
}

/** 文本输入编辑控件 */
const TextEditControl = ({
  label,
  draft,
  placeholder,
  onDraftChange,
  onCommit,
  onCancel,
}: Omit<DetailRowEditControlProps, "type" | "dictCode">) => {
  /** Enter 确认，Esc 取消 */
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onCommit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <Input
      size="small"
      className="flex-1 min-w-0 text-xs"
      value={draft}
      placeholder={placeholder ?? `请输入${label}`}
      autoFocus
      onChange={(e) => onDraftChange(e.target.value)}
      onBlur={() => onCommit()}
      onKeyDown={handleKeyDown}
    />
  );
};

/** 字典下拉编辑控件 */
const DictEditControl = ({
  label,
  dictCode,
  draft,
  placeholder,
  onCommit,
  onCancel,
}: DetailRowEditControlProps & { dictCode: string }) => {
  return (
    <DictSelect
      typeCode={dictCode}
      size="small"
      className="flex-1 min-w-0"
      style={{ width: "100%" }}
      value={draft || undefined}
      placeholder={placeholder ?? `请选择${label}`}
      autoFocus
      defaultOpen
      searchable
      onChange={(next) => onCommit(next ?? "")}
      onBlur={() => {
        // 延迟判断，避免与 onChange 选中提交冲突
        window.setTimeout(() => onCancel(), 200);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
    />
  );
};

/**
 * DetailRow 行内编辑控件分发
 * 按 type 渲染对应编辑器，后续可扩展 date、file 等
 */
export const DetailRowEditControl = ({ type, dictCode, ...rest }: DetailRowEditControlProps) => {
  if (type === "dict" && dictCode) {
    return <DictEditControl type={type} dictCode={dictCode} {...rest} />;
  }
  return <TextEditControl {...rest} />;
};
